'use client'

import { SessionStorageHelper } from 'helpers/sessionStorageHelper'
import {
  incrementReadingTimeSummary,
  saveReadingTimeToLocalStorage,
} from 'helpers/readingTime'
import { useUserData } from 'contexts/UserDataContext'
import { useCallback, useEffect, useRef, useState } from 'react'

interface StopwatchProps {
  width?: string | number
  className?: string
  getTime: (_value: string) => void
}

const STORAGE_KEYS = {
  COUNTER: 'stopwatch_counter',
  IS_RUNNING: 'stopwatch_is_running',
}

const formatTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export default function Stopwatch({
  width = '100px',
  className = '',
  getTime,
}: StopwatchProps) {
  const { userData, updateMultipleUserData } = useUserData()
  const [counter, setCounter] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const getTimeRef = useRef(getTime)
  const lastSavedCounterRef = useRef(0)

  // Keep ref in sync with latest prop
  useEffect(() => {
    getTimeRef.current = getTime
  }, [getTime])

  const saveReadingTimeToStats = useCallback(
    (seconds: number): void => {
      if (seconds <= 0) return

      // Update LocalStorage
      saveReadingTimeToLocalStorage(seconds)

      // Sync to cloud via UserDataContext
      if (userData.readingTime) {
        const updatedReadingTime = incrementReadingTimeSummary(
          seconds,
          userData.readingTime,
        )
        updateMultipleUserData({ readingTime: updatedReadingTime })
      }
    },
    [userData.readingTime, updateMultipleUserData],
  )

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const saveToSession = useCallback((count: number, running: boolean) => {
    SessionStorageHelper.set(STORAGE_KEYS.COUNTER, count)
    SessionStorageHelper.set(STORAGE_KEYS.IS_RUNNING, running)
    SessionStorageHelper.set('stopWatchStatus', running) // Keep backward compatibility
  }, [])

  const startTimer = useCallback(() => {
    clearTimer()
    saveToSession(counter, true)

    intervalRef.current = setInterval(() => {
      setCounter((prev) => {
        const newCount = prev + 1
        getTimeRef.current(formatTime(newCount))
        // Save counter every 5 seconds to prevent data loss
        if (newCount % 5 === 0) {
          SessionStorageHelper.set(STORAGE_KEYS.COUNTER, newCount)
        }
        return newCount
      })
    }, 1000)
  }, [clearTimer, saveToSession, counter])

  const stopTimer = useCallback(() => {
    clearTimer()

    // Calculate time spent since last save and store to daily reading time
    const timeSpent = counter - lastSavedCounterRef.current
    if (timeSpent > 0) {
      saveReadingTimeToStats(timeSpent)
      lastSavedCounterRef.current = counter
    }

    saveToSession(counter, false)
  }, [clearTimer, saveToSession, counter, saveReadingTimeToStats])

  const handleClick = useCallback(() => {
    if (isRunning) {
      stopTimer()
    } else {
      startTimer()
    }
    setIsRunning((prev) => !prev)
  }, [isRunning, startTimer, stopTimer])

  const handleReset = useCallback(() => {
    clearTimer()

    // Save remaining time before reset
    const timeSpent = counter - lastSavedCounterRef.current
    if (timeSpent > 0) {
      saveReadingTimeToStats(timeSpent)
    }

    setCounter(0)
    setIsRunning(false)
    lastSavedCounterRef.current = 0
    getTimeRef.current(formatTime(0))
    saveToSession(0, false)
  }, [clearTimer, saveToSession, counter, saveReadingTimeToStats])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleReset()
      }
    },
    [handleReset],
  )

  // Initialize from sessionStorage on mount
  useEffect(() => {
    const savedCounter = SessionStorageHelper.get(STORAGE_KEYS.COUNTER)
    const savedIsRunning = SessionStorageHelper.get(STORAGE_KEYS.IS_RUNNING)

    let initialCounter = 0
    let shouldAutoStart = true

    if (savedCounter) {
      const parsedCounter = JSON.parse(savedCounter)
      if (typeof parsedCounter === 'number') {
        // Just restore the saved counter - don't add elapsed time
        // This ensures we only count time when stopwatch is present on the page
        initialCounter = parsedCounter
      }
    }

    // Check if it was previously running
    if (savedIsRunning !== null) {
      shouldAutoStart = savedIsRunning === 'true'
    }

    setCounter(initialCounter)
    lastSavedCounterRef.current = initialCounter
    getTimeRef.current(formatTime(initialCounter))
    setIsInitialized(true)

    if (shouldAutoStart) {
      setIsRunning(true)
    }

    return () => clearTimer()
  }, [clearTimer])

  // Start timer after initialization if needed
  useEffect(() => {
    if (isInitialized && isRunning && !intervalRef.current) {
      startTimer()
    }
  }, [isInitialized, isRunning, startTimer])

  // Use refs for cleanup to avoid stale closure issues
  const counterRef = useRef(counter)
  const isRunningRef = useRef(isRunning)

  useEffect(() => {
    counterRef.current = counter
    isRunningRef.current = isRunning
  }, [counter, isRunning])

  // Store the latest save function in a ref to use in unmount without triggering effect reruns
  const saveReadingTimeToStatsRef = useRef(saveReadingTimeToStats)
  useEffect(() => {
    saveReadingTimeToStatsRef.current = saveReadingTimeToStats
  }, [saveReadingTimeToStats])

  // Save state when component unmounts (navigating away from page with stopwatch)
  // This ensures we only count time when the stopwatch is actually present
  useEffect(() => {
    return () => {
      // Save reading time on unmount
      const timeSpent = counterRef.current - lastSavedCounterRef.current
      if (timeSpent > 0) {
        saveReadingTimeToStatsRef.current(timeSpent)
        lastSavedCounterRef.current = counterRef.current
      }
      // Save counter and running state (preserve state for pages with stopwatch)
      SessionStorageHelper.set(STORAGE_KEYS.COUNTER, counterRef.current)
      SessionStorageHelper.set(STORAGE_KEYS.IS_RUNNING, isRunningRef.current)
      SessionStorageHelper.set('stopWatchStatus', isRunningRef.current)
    }
  }, [])

  // Save to localStorage before page unload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeSpent = counter - lastSavedCounterRef.current
      if (timeSpent > 0) {
        saveReadingTimeToStats(timeSpent)
      }
      saveToSession(counter, isRunning)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [counter, isRunning, saveToSession, saveReadingTimeToStats])

  return (
    <button
      type="button"
      onClick={handleClick}
      onDoubleClick={handleReset}
      onKeyDown={handleKeyDown}
      style={{ width }}
      aria-pressed={isRunning}
      title="Başlat/Durdurmak için tıklayın, sıfırlamak için çift tıklayın veya Delete tuşuna basın."
      className={`
        flex items-center justify-center
        h-[34px] px-3 rounded-full
        cursor-pointer select-none
        border-2 transition-colors duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2
        ${
          isRunning
            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
            : 'border-zinc-400 dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
        }
        ${className}
      `}
    >
      <span className="sr-only">Kronometre: </span>
      <span className="font-semibold tabular-nums">{formatTime(counter)}</span>
    </button>
  )
}
